import { useState, useRef, useEffect } from 'react'
import { Bell, BellOff, Check, AlertTriangle, HardDrive } from 'lucide-react'
import { useNotifications, type AppNotification } from '../hooks/useNotifications'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'

export function NotificationBell() {
  const { t } = useTranslation()
  const { isConnected, notifications, clearNotifications, requestPermission, permissionStatus } = useNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const unreadCount = notifications.length

  const handleEnableNotifications = (e: React.MouseEvent) => {
    e.stopPropagation()
    requestPermission()
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          isConnected
            ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            : 'text-gray-400 dark:text-gray-600'
        }`}
        title={isConnected ? t('notifications.connected') : t('notifications.disconnected')}
      >
        {isConnected ? <Bell size={20} /> : <BellOff size={20} />}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Bell size={16} />
              {t('notifications.title')}
            </h3>
            {notifications.length > 0 && (
              <button
                onClick={clearNotifications}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                {t('notifications.clearAll')}
              </button>
            )}
          </div>

          {permissionStatus !== 'granted' && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              <p className="text-xs text-amber-800 dark:text-amber-200 mb-2">
                {t('notifications.enablePrompt')}
              </p>
              <button
                onClick={handleEnableNotifications}
                className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded-lg transition-colors"
              >
                {t('notifications.enable')}
              </button>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <BellOff size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('notifications.empty')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {notifications.map((notification, index) => (
                  <NotificationItem key={`${notification.timestamp}-${index}`} notification={notification} />
                ))}
              </ul>
            )}
          </div>

          <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              {isConnected ? t('notifications.connected') : t('notifications.disconnected')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function NotificationItem({ notification }: { notification: AppNotification }) {
  if (notification.type === 'backup') {
    return (
      <li className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
        notification.success ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
      }`}>
        <div className="flex items-start gap-2">
          {notification.success ? (
            <HardDrive size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {notification.title}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
              {notification.message}
            </p>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
            </p>
          </div>
        </div>
      </li>
    )
  }

  // Alert notification (default)
  return (
    <li className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
      notification.isRecovery ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
    }`}>
      <div className="flex items-start gap-2">
        {notification.isRecovery ? (
          <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
        ) : (
          <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {notification.title}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
            {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
          </p>
        </div>
      </div>
    </li>
  )
}
